import { NodeType } from '@/types/graph'
import { taxonomyFor, type DrawingDiscipline } from '@/config/classification'

/** Internal config the draw handlers (`handleAdd` / `handlePolygonAdd`) read to
 *  build the node for a freshly drawn shape. */
export interface AddNodeConfig {
  type: NodeType
  label?: string
  note?: string
  annotation_text?: string
  pnid_level_i?: string
  pnid_level_ii?: string
  pnid_level_iii?: string
  detectConnections?: boolean
  // Draw shape for the Add-Object flow: 'polygon' traces a freeform outline;
  // 'line' is a Piping/Instrumentation Line connection; 'point' drops a single
  // junction dot (ENG-2613); default/undefined is the existing drag-rectangle.
  shape?: 'rectangle' | 'polygon' | 'line' | 'point'
}

/** What the V2 Add-Object panel (`AddNodePanelV2`) emits as the user pre-fills it. */
export interface AddNodePanelConfig {
  category: string
  subcategory: string
  type: string
  label: string
  note: string
  annotationText: string
  shape: 'rectangle' | 'polygon' | 'line' | 'point'
}

/**
 * Map the Add-Object panel's pre-fill into the internal `AddNodeConfig` the draw
 * handlers consume. The node *kind* is derived from the chosen classification
 * (mirrors the V1 ControlPanel): Piping/Instrumentation Line => Connection,
 * Annotation Tag => Tag, otherwise Symbol.
 *
 * Carries the Tag (`label`) and `note` through — these were previously dropped
 * here, so drawing a box after pre-filling them produced a node with no tag
 * even though every other field (category/subcategory/type/shape) carried.
 */
export function panelConfigToAddNodeConfig(
  config: AddNodePanelConfig,
  // Which sheet the picked category belongs to. Defaults to P&ID, which is
  // every drawing that existed before SLD annotation.
  discipline: DrawingDiscipline = 'pnid',
): AddNodeConfig {
  const taxonomy = taxonomyFor(discipline)
  const kind = config.category
    ? taxonomy.resolveNodeType(config.category, config.subcategory, config.type)
    : 'symbol'
  return {
    type:
      kind === 'connection'
        ? NodeType.Connection
        : kind === 'tag'
        ? NodeType.Tag
        : NodeType.Symbol,
    pnid_level_i: config.category,
    pnid_level_ii: config.subcategory,
    pnid_level_iii: config.type,
    label: config.label,
    note: config.note,
    annotation_text: config.annotationText,
    shape: config.shape,
  }
}
